import { type ComponentProps, memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

import { PageHeaderTrailingSlot } from '~/components/v2/components/shared/page-header/PageHeaderTrailingSlot';
import { useWorkspaceSplitPageTopbar } from '~/components/v2/components/shared/panel/useWorkspaceSplitPageTopbar';
import { useWorkspaceNavPushMode } from '~/components/v2/hooks/useWorkspaceNavPushMode';

export type BoardHeaderProps = {
  actions?: ReactNode;
  compact?: boolean;
  customFields?: ReactNode;
  description?: ReactNode;
  headerActions?: ReactNode;
  tabs?: ReactNode;
  title: ReactNode;
  viewActions?: ReactNode;
  viewActionsClassName?: Pick<ComponentProps<'div'>, 'className'>['className'];
};

export const BoardHeader = memo(
  ({
    actions,
    compact = false,
    description,
    customFields,
    headerActions,
    tabs,
    title,
    viewActions,
    viewActionsClassName,
  }: BoardHeaderProps) => {
    const { isWorkspaceNavPushMode } = useWorkspaceNavPushMode();
    const showSplitPageTopbar = useWorkspaceSplitPageTopbar();
    const hasTabs = tabs !== undefined;
    const hasViewActions = viewActions !== undefined || showSplitPageTopbar;

    return (
      <header
        className={cn(
          'flex flex-col px-4',
          compact ? 'pt-2' : 'pt-3 ui2026:pt-4',
          hasTabs && (compact ? 'gap-0 pb-0' : 'gap-1 ui2026:gap-3 pb-0'),
          !hasTabs && 'border-b ads:border-b-default border-b-gray-4',
          !hasTabs && (compact ? 'pb-2' : 'pb-4'),
          isWorkspaceNavPushMode && !compact && 'min-h-[120px]',
        )}
      >
        <div
          className={cn(
            // Without tabs the view actions sit in this row, so it's the page-items-stats container for them (see the
            // tabs row below for why a container query).
            'flex gap-3',
            !hasTabs && 'ui2026:@container/page-items-stats',
            isWorkspaceNavPushMode && 'flex-col',
            !isWorkspaceNavPushMode && !hasTabs && 'items-end',
            !isWorkspaceNavPushMode && hasTabs && 'items-start',
          )}
        >
          <div className={cn('min-w-0 grow', isWorkspaceNavPushMode && 'w-full', hasTabs && 'ui2026:pl-2')}>
            <div className="flex items-center ui2026:gap-1">
              <div className="min-w-0 font-semibold ads:text-primary text-24 text-gray-12">{title}</div>
              {actions}
            </div>
            {description}
            {customFields}
          </div>
          {!hasTabs && hasViewActions && (
            <div className={cn('shrink-0', isWorkspaceNavPushMode && 'overflow-x-auto', viewActionsClassName)}>
              <PageHeaderTrailingSlot>{viewActions}</PageHeaderTrailingSlot>
            </div>
          )}
          {headerActions && <div className="shrink-0">{headerActions}</div>}
        </div>
        {hasTabs && (
          // Named container so BoardHeaderStats can drop to a bare count before this row wraps the view actions onto
          // their own line — no JS measuring. container-type: inline-size is safe here because the row's width comes
          // from the header, not from its content.
          <div className="ui2026:@container/page-items-stats -mx-4 flex ui2026:flex-wrap items-center justify-between gap-3 ui2026:gap-y-2 border-b ads:border-b-default border-b-gray-4">
            <div className="min-w-0 shrink-0 pl-6">{tabs}</div>
            {hasViewActions && (
              // ml-auto keeps the cluster right-aligned when it wraps to its own line (auto margins resolve per flex line).
              <div
                className={cn(
                  'ui2026:ml-auto min-w-0 shrink pr-4',
                  isWorkspaceNavPushMode && 'overflow-x-auto',
                  viewActionsClassName,
                )}
              >
                <PageHeaderTrailingSlot>{viewActions}</PageHeaderTrailingSlot>
              </div>
            )}
          </div>
        )}
      </header>
    );
  },
);

BoardHeader.displayName = 'BoardHeader';
